/**
 * Transformasjoner for native paneler (kpi/chart/table/heatmap/funnel/treemap) i Compass-visninger.
 * Rene funksjoner — kjøres i nettleseren på datasettets rader, slik at ett datasett kan gi mange paneler
 * og tallene alltid kommer fra dataene (ingen tokens ved visning).
 */
import type { KpiItemSpec, TransformStep } from './types';

type Row = Record<string, any>;

const toNumber = (v: unknown): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
};

const compare = (a: unknown, b: unknown): number => {
    if (a === b) return 0;
    if (a === null || a === undefined) return 1;
    if (b === null || b === undefined) return -1;
    const na = toNumber(a);
    const nb = toNumber(b);
    if (na !== null && nb !== null && typeof a !== 'string') return na - nb;
    return String(a).localeCompare(String(b), 'nb', { numeric: true });
};

function applyFilter(rows: Row[], f: { field: string; op: string; value?: unknown }): Row[] {
    return rows.filter(r => {
        const v = r[f.field];
        switch (f.op) {
            case 'eq': return v == f.value; // eslint-disable-line eqeqeq
            case 'neq': return v != f.value; // eslint-disable-line eqeqeq
            case 'gt': return compare(v, f.value) > 0;
            case 'gte': return compare(v, f.value) >= 0;
            case 'lt': return compare(v, f.value) < 0;
            case 'lte': return compare(v, f.value) <= 0;
            case 'in': return Array.isArray(f.value) && f.value.some(x => x == v); // eslint-disable-line eqeqeq
            case 'notnull': return v !== null && v !== undefined && v !== '';
            default: return true;
        }
    });
}

export function aggregateValues(values: unknown[], op: string): number | string | null {
    switch (op) {
        case 'count': return values.length;
        case 'distinct': return new Set(values.filter(v => v !== null && v !== undefined).map(v => String(v))).size;
        case 'first': return (values[0] as any) ?? null;
        case 'last': return (values[values.length - 1] as any) ?? null;
    }
    const nums = values.map(toNumber).filter((n): n is number => n !== null);
    if (nums.length === 0) return null;
    switch (op) {
        case 'sum': return nums.reduce((a, b) => a + b, 0);
        case 'avg': return nums.reduce((a, b) => a + b, 0) / nums.length;
        case 'min': return Math.min(...nums);
        case 'max': return Math.max(...nums);
        default: return null;
    }
}

function applyAggregate(rows: Row[], a: { groupBy?: string[]; measures: { field?: string; op: string; as: string }[] }): Row[] {
    const groupBy = a.groupBy ?? [];
    const groups = new Map<string, Row[]>();
    for (const r of rows) {
        const key = JSON.stringify(groupBy.map(g => r[g] ?? null));
        const list = groups.get(key);
        if (list) list.push(r); else groups.set(key, [r]);
    }
    if (groups.size === 0 && groupBy.length === 0) groups.set('[]', []);
    const out: Row[] = [];
    groups.forEach(list => {
        const row: Row = {};
        for (const g of groupBy) row[g] = list[0]?.[g] ?? null;
        for (const m of a.measures) {
            row[m.as] = aggregateValues(m.op === 'count' ? list : list.map(r => (m.field ? r[m.field] : null)), m.op);
        }
        out.push(row);
    });
    return out;
}

function applyTopN(rows: Row[], t: { n: number; field: string; labelField: string; othersLabel?: string }): Row[] {
    const sorted = [...rows].sort((a, b) => compare(b[t.field], a[t.field]));
    // Uten gyldig n: bare sortering (ellers ble «Øvrige» lik totalen og summen dobbelttelt)
    if (!(Number.isInteger(t.n) && t.n > 0) || sorted.length <= t.n) return sorted;
    const top = sorted.slice(0, t.n);
    const rest = sorted.slice(t.n);
    const others: Row = { [t.labelField]: t.othersLabel ?? 'Øvrige' };
    others[t.field] = rest.reduce((sum, r) => sum + (toNumber(r[t.field]) ?? 0), 0);
    return [...top, others];
}

/** Evaluerer «[A] / [B] * 100» uten eval: kun felt, tall, + - * / og parenteser. */
export function evaluateDerive(expr: string, row: Row): number | null {
    const tokens = expr.match(/\[[^[\]]+\]|\d+(?:\.\d+)?|[-+*/()]/g) ?? [];
    let pos = 0;
    const peek = () => tokens[pos];
    const next = () => tokens[pos++];

    const primary = (): number | null => {
        const tok = next();
        if (tok === undefined) return null;
        if (tok === '(') { const v = expression(); next(); return v; }
        if (tok === '-') { const v = primary(); return v === null ? null : -v; }
        if (tok.startsWith('[')) return toNumber(row[tok.slice(1, -1)]);
        return toNumber(tok);
    };
    const term = (): number | null => {
        let left = primary();
        while (peek() === '*' || peek() === '/') {
            const op = next();
            const right = primary();
            if (left === null || right === null) { left = null; continue; }
            left = op === '*' ? left * right : right === 0 ? null : left / right;
        }
        return left;
    };
    const expression = (): number | null => {
        let left = term();
        while (peek() === '+' || peek() === '-') {
            const op = next();
            const right = term();
            if (left === null || right === null) { left = null; continue; }
            left = op === '+' ? left + right : left - right;
        }
        return left;
    };
    const value = expression();
    return value === null || !Number.isFinite(value) ? null : value;
}

export function applyTransforms(rows: Row[], steps?: TransformStep[]): Row[] {
    let current = rows;
    for (const step of steps ?? []) {
        if ('filter' in step) current = applyFilter(current, step.filter);
        else if ('aggregate' in step) current = applyAggregate(current, step.aggregate);
        else if ('sort' in step) {
            const keys = step.sort;
            current = [...current].sort((a, b) => {
                for (const k of keys) {
                    const c = compare(a[k.field], b[k.field]);
                    if (c !== 0) return k.dir === 'desc' ? -c : c;
                }
                return 0;
            });
        } else if ('limit' in step) current = current.slice(0, step.limit);
        else if ('topN' in step) current = applyTopN(current, step.topN);
        else if ('derive' in step) {
            const d = step.derive;
            current = current.map(r => ({ ...r, [d.as]: evaluateDerive(d.expr, r) }));
        }
    }
    return current;
}

/** Beregner ett nøkkeltall fra radene. */
export function computeKpi(rows: Row[], item: KpiItemSpec): number | string | null {
    const op = item.aggregate ?? 'sum';
    if (op === 'count') return rows.length;
    return aggregateValues(rows.map(r => (item.field ? r[item.field] : null)), op);
}

/** Tolker terskel som «>= 95» og sjekker verdien. */
export function matchesThreshold(value: number | string | null, threshold?: string): boolean {
    if (!threshold || typeof value !== 'number') return false;
    const m = threshold.trim().match(/^(>=|<=|!=|>|<|=)\s*(-?\d+(?:\.\d+)?)$/);
    if (!m) return false;
    const n = Number(m[2]);
    switch (m[1]) {
        case '>=': return value >= n;
        case '<=': return value <= n;
        case '>': return value > n;
        case '<': return value < n;
        case '!=': return value !== n;
        default: return value === n;
    }
}
